<script lang="ts">
  // Which buyer's pages to work on. Pages belong to the relation, so this list is the
  // only way into the editor — there is no "our catalogue" to edit once and send to
  // everyone, because that is precisely how one buyer ends up seeing another's terms.

  import { t } from '$lib/i18n/index.svelte';
  import { asClientError } from '$lib/protocol/errors';
  import { operatorThreads, type StaffThread } from '$lib/protocol/operator-api';
  import { loadStaffState, signOutStaff, staffServerUrl, staffSession } from '$lib/state/staff.svelte';

  import { navigate } from '../../../app/router.svelte';
  import EmptyState from '../../parts/EmptyState.svelte';

  let threads = $state<StaffThread[]>([]);
  let problem = $state('');
  let busy = $state(true);

  $effect(() => {
    void (async () => {
      await loadStaffState();
      const session = staffSession();
      if (session === undefined) {
        navigate({ name: 'staffLogin' });
        return;
      }
      try {
        threads = await operatorThreads({ serverUrl: staffServerUrl(), token: session.token });
      } catch (error) {
        problem = asClientError(error).message;
      } finally {
        busy = false;
      }
    })();
  });
</script>

<header class="bar">
  <h1>{t('staff.buyers')}</h1>
  <button
    class="out"
    onclick={() => void signOutStaff().then(() => navigate({ name: 'staffLogin' }))}
  >
    {t('staff.signOut')}
  </button>
</header>

{#if problem.length > 0}
  <p class="problem" role="alert">{problem}</p>
{:else if busy}
  <p class="hint">{t('views.loading')}</p>
{:else if threads.length === 0}
  <EmptyState title={t('staff.buyers')} body={t('staff.noBuyers')} />
{:else}
  <ul>
    {#each threads as thread (thread.threadId)}
      <li>
        <button onclick={() => navigate({ name: 'staffViews', threadId: thread.threadId })}>
          {thread.buyerName.length > 0 ? thread.buyerName : thread.threadId}
        </button>
      </li>
    {/each}
  </ul>
{/if}

<style>
  .bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
  }
  h1 {
    margin: 0;
    font-size: 20px;
  }
  .out {
    border: 0;
    background: transparent;
    color: var(--ink-soft);
    font: inherit;
    cursor: pointer;
  }
  ul {
    list-style: none;
    margin: 0;
    padding: 0 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow-y: auto;
  }
  li button {
    width: 100%;
    text-align: left;
    border: 1px solid var(--line);
    border-radius: var(--radius);
    background: var(--surface);
    color: var(--ink);
    font: inherit;
    padding: 12px;
    cursor: pointer;
  }
  .problem,
  .hint {
    margin: 0 16px;
    font-size: 13px;
    color: var(--ink-soft);
  }
</style>
